/**
 * ============================================================================
 * Promotion Formatters - Business logic utilities
 * ============================================================================
 *
 * Extracted from JSX for testability and reusability
 *
 * ============================================================================
 */

import type { Promotion } from '../types/promotions';

/**
 * Format date range
 */
export function formatDateRange(validFrom: string, validUntil: string): string {
  try {
    const from = new Date(validFrom);
    const until = new Date(validUntil);

    const fromStr = from.toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
    });

    const untilStr = until.toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${fromStr} - ${untilStr}`;
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format time range
 */
export function formatTimeRange(startTime: string | null, endTime: string | null): string {
  if (!startTime || !endTime) return 'All day';

  try {
    const start = startTime.substring(0, 5); // HH:MM
    const end = endTime.substring(0, 5); // HH:MM
    return `${start} - ${end}`;
  } catch {
    return 'All day';
  }
}

/**
 * Format days of week
 */
export function formatDaysOfWeek(days: string[] | null): string {
  if (!days || days.length === 0) return 'All days';
  if (days.length === 7) return 'All days';

  const dayAbbr: Record<string, string> = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };

  return days.map((day) => dayAbbr[day] || day).join(', ');
}

/**
 * Get promotion status
 */
export function getPromotionStatus(promotion: Promotion): {
  status: 'active' | 'scheduled' | 'expired' | 'inactive';
  label: string;
  colorClass: string;
} {
  const now = new Date();
  const validFrom = new Date(promotion.valid_from);
  const validUntil = new Date(promotion.valid_until);

  if (!promotion.is_active) {
    return {
      status: 'inactive',
      label: 'Inactive',
      colorClass: 'bg-gray-100 text-gray-700',
    };
  }

  if (now < validFrom) {
    return {
      status: 'scheduled',
      label: 'Scheduled',
      colorClass: 'bg-blue-100 text-blue-700',
    };
  }

  if (now > validUntil) {
    return {
      status: 'expired',
      label: 'Expired',
      colorClass: 'bg-red-100 text-red-700',
    };
  }

  return {
    status: 'active',
    label: 'Active',
    colorClass: 'bg-green-100 text-green-700',
  };
}

/**
 * Check if promotion is active
 */
export function isPromotionActive(promotion: Promotion): boolean {
  const now = new Date();
  const validFrom = new Date(promotion.valid_from);
  const validUntil = new Date(promotion.valid_until);

  return promotion.is_active && now >= validFrom && now <= validUntil;
}

/**
 * Format discount percentage
 */
export function formatDiscount(percentage: number | string): string {
  const percentNum = typeof percentage === 'string' ? parseFloat(percentage) : percentage;
  return `${percentNum.toFixed(0)}% OFF`;
}

/**
 * Export promotions to CSV
 */
export function exportPromotionsToCSV(promotions: Promotion[]): void {
  if (promotions.length === 0) {
    throw new Error('No promotions to export');
  }

  const headers = [
    'Title',
    'Description',
    'Discount %',
    'Valid From',
    'Valid Until',
    'Days of Week',
    'Time Range',
    'Sport',
    'Status',
    'Created By',
  ];

  const rows = promotions.map((promo) => [
    promo.title || '',
    promo.description || '',
    promo.discount_percentage.toString(),
    promo.valid_from,
    promo.valid_until,
    formatDaysOfWeek(promo.days_of_week),
    formatTimeRange(promo.start_time, promo.end_time),
    promo.sport?.name || 'All Sports',
    getPromotionStatus(promo).label,
    promo.created_by,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `promotions_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
